/// <amd-module name="VSS/Diagnostics/Dialog/DiagnosticsListPivot" />
import React = require("react");

import { ClientContribution as Contribution } from "VSS/Platform/Contracts/Contributions";
import { ContributionLink } from "VSS/Diagnostics/Dialog/DiagnosticsDialog.types";
import { Filter, FILTER_CHANGE_EVENT } from "VSSUI/Utilities/Filter";
import { FilterBar } from "VSSUI/FilterBar";
import { KeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { IVssComponentProperties, VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { memoizeFunction } from "VSS/Core/Util/Memoize";
import { PerfDiagnosticsListPivotContent } from "VSS/Diagnostics/Dialog/DiagnosticsListPivotContent";
import { PickListFilterBarItem } from "VSSUI/PickList";
import { SelectionMode } from "OfficeFabric/Selection";

export interface PerfDiagnosticsListPivotProps extends IVssComponentProperties {
    contributions: Contribution[];
    allContributions: Contribution[];
    chosenContributions: Contribution[];
    targets: { [contributionId: string]: ContributionLink[] };
    includes: { [contributionId: string]: ContributionLink[] };
}

export interface PerfDiagnosticsListPivotState extends IVssComponentProperties {
    filter: Filter;
    filteredItems: Contribution[];
}

export class PerfDiagnosticsListPivot extends VssComponent<PerfDiagnosticsListPivotProps, PerfDiagnosticsListPivotState> {
    constructor(props: PerfDiagnosticsListPivotProps, context: IVssComponentContext) {
        super(props, context);

        const filter = new Filter();
        filter.subscribe(this.onFilterChanged, FILTER_CHANGE_EVENT);

        const { chosenContributions } = this.props;

        let typeCount: { [contributionId: string]: number } = {};
        chosenContributions.forEach(function (element) {
            let type: string = element.type;
            if (!type) {
                type = "No type";
            }
            if (!typeCount[type]) {
                typeCount[type] = 1;
            } else {
                typeCount[type] = typeCount[type] + 1;
            }
        });

        this.state = {
            filter,
            filteredItems: chosenContributions,
            typeCount: typeCount
        };
    }

    typeCount: { [contributionId: string]: number } = {};

    menuButtonElement: Element | null = null;

    public render(): JSX.Element {
        const { filter, filteredItems } = this.state;
        const { contributions, allContributions, targets, includes, chosenContributions } = this.props;

        const typeCount = this.updateTypeCount(chosenContributions);
        const filteredItemsShown = this.updateFilteredList(chosenContributions, filter, filteredItems);

        return (
            <div className="flex flex-grow flex-column">
                <FilterBar filter={filter}>
                    <KeywordFilterBarItem placeholder="Search by Contribution Id" filterItemKey="keyword" maxTextLength={50} />
                    <KeywordFilterBarItem placeholder="Search properties" filterItemKey="properties" maxTextLength={50} />
                    <PickListFilterBarItem
                        isSearchable={true}
                        placeholder="Contribution Types"
                        filterItemKey="typeSelection"
                        selectionMode={SelectionMode.multiple}
                        getPickListItems={() => Object.keys(typeCount).sort()}
                        indicators={[
                            {
                                getItemIndicator: (item: string) => {
                                    if (!item) {
                                        return null;
                                    }
                                    return { title: String(typeCount[item]) };
                                }
                            }
                        ]}
                    />
                </FilterBar>
                <PerfDiagnosticsListPivotContent
                    filteredItems={filteredItemsShown}
                    contributions={contributions}
                    allContributions={allContributions}
                    targets={targets}
                    includes={includes}
                />
            </div>
        );
    }

    public componentWillUnmount() {
        const { filter } = this.state;
        filter.unsubscribe(this.onFilterChanged, FILTER_CHANGE_EVENT);
    }

    private updateFilteredList = memoizeFunction((chosenContributions: Contribution[], filter: Filter, filteredItemsOld: Contribution[]) => {
        return this.filterContributions(chosenContributions, filter);
    });

    private updateTypeCount = memoizeFunction((chosenContributions: Contribution[]) => {
        let typeCount: { [contributionId: string]: number } = {};
        chosenContributions.forEach(function (element) {
            let type: string = element.type;
            if (!type) {
                type = "No type";
            }
            if (!typeCount[type]) {
                typeCount[type] = 1;
            } else {
                typeCount[type] = typeCount[type] + 1;
            }
        });
        return typeCount;
    });

    private onFilterChanged = () => {
        const filteredItems = this.filterContributions(this.props.chosenContributions, this.state.filter);
        this.setState({
            filteredItems: filteredItems
        });
    };

    private filterContributions(contributions: Contribution[], filter: Filter) {
        let filteredItems: Contribution[] = [...contributions];

        const filterKeywords = filter.getFilterItemValue<string>("keyword");
        if (filterKeywords) {
            filteredItems = filteredItems.filter(item => (item.id || "").indexOf(filterKeywords) >= 0);
        }

        const filterTypeSelection = filter.getFilterItemValue<string[]>("typeSelection");
        if (filterTypeSelection && filterTypeSelection.length) {
            filteredItems = filteredItems.filter(item =>
                filterTypeSelection.some(type => type === item.type || (type === "No type" && item.type === ""))
            );
        }

        const propsKeywords = filter.getFilterItemValue<string>("properties");
        if (propsKeywords) {
            const lowerPropsKeywords = propsKeywords.toLocaleLowerCase();
            filteredItems = filteredItems.filter(item => JSON.stringify(item.properties).toLocaleLowerCase().indexOf(lowerPropsKeywords) >= 0);
        }

        return filteredItems;
    }
}
